import React, { useEffect, useState } from "react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import SettingsHeader from "./SettingsHeader";
import { Link, useLocation } from "react-router-dom";
import { CONTACT_SOCIAL_LINKS } from "./contactSocialLinks";

import {
  ChevronRight,
  Mail,
  Phone,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
} from "lucide-react";

const ContactUs = () => {
  const location = useLocation();
  const data = location.state || {};
  const [formData, setFormData] = useState({
    Name: "",
    Email: "",
    Message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setFormErrors({ ...formErrors, [name]: "" });
  };

const validateEmail = (email) => {
    // Check for double at symbols
    if (email.includes('@@')) {
        return false;
    }
    
    // Check for double dots
    if (email.includes('..')) {
        return false;
    }
    
    // Original regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return emailRegex.test(email);
};

  const validateMessage = (msg) => {
    const trimmed = msg.trim();
    const onlySpecialCharRegex = /^[.,/?!@#$%^&*()_+=\-'"`~<>{}|\\[\];:]+$/;
    const specialCharExistRegex = /[.,/?!@#$%^&*()_+=\-'"`~<>{}|\\[\];:]/;

    return (
      trimmed.length > 10 &&
      !onlySpecialCharRegex.test(trimmed) &&
      !specialCharExistRegex.test(trimmed)
    );
  };

  const validateName = (name) => {
    const trimmed = name.trim();
    const invalidCharRegex = /[^a-zA-Z\s]/;
    return trimmed.length >= 2 && !invalidCharRegex.test(trimmed);
  };

  const validateForm = () => {
    let errors = {};

    if (!formData.Name || !validateName(formData.Name)) {
      errors.Name =
        "Please enter a valid name (min 2 characters, no special characters)";
    }

    if (!formData.Email || !validateEmail(formData.Email)) {
      errors.Email = "Please enter a valid email address";
    }

    if (!formData.Message || !validateMessage(formData.Message)) {
      errors.Message =
        "Please enter a proper message (min 10 characters, no special characters)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = JSON.parse(localStorage.getItem("token")) || "";

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/contectUs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.status === 200) {
        setSuccess(true);
        setFormData({ Name: "", Email: "", Message: "" });
        setFormErrors({});
      } else {
        throw new Error("Failed to send message");
      }
    } catch (err) {
      setError(err.message || "An error occurred while sending your message");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <SettingsHeader name="Contact us" />
      {data.public ?? (
        <div className="flex gap-2 mb-6 absolute left-12 top-28">
          <Link to={`/settings`}>Settings</Link>
          <ChevronRight />
          <p className="text-gray-700">Contact us</p>
        </div>
      )}

      <div className="my-14 w-full px-4 md:px-12 lg:px-20">
        <p className="text-center mt-2 mb-8">
          Any questions or remarks? Just write us a message!
        </p>

        <div className="flex flex-col md:flex-row gap-8 w-full max-w-6xl mx-auto">
          <div className="bg-lime-300 rounded-2xl p-8 flex flex-col justify-between w-full md:w-2/5">
            <div>
              <h2 className="text-2xl font-bold mb-2">Contact information</h2>
              <p className="mb-8">
                Fill up the form and our team will get back to you within 24
                hours
              </p>

              <div className="flex items-center mb-6">
                <Mail className="mr-4" size={20} />
                <span>support@mathdingo.com</span>
              </div>

              {/* <div className="flex items-center mb-6">
                <Phone className="mr-4" size={20} />
                <span>+61 439 756 987</span>
              </div> */}
            </div>

            <div className="flex space-x-6 mt-6">
              {CONTACT_SOCIAL_LINKS.map(({ id, label, href }) => {
                const Icon =
                  id === "youtube"
                    ? Youtube
                    : id === "linkedin"
                      ? Linkedin
                      : id === "instagram"
                        ? Instagram
                        : Facebook;

                return (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                  >
                    <Icon size={24} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="w-full md:w-3/5">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="Name" className="block mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="Name"
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  placeholder="Your Name here"
                  className={`w-full p-4 rounded-lg border ${
                    formErrors.Name ? "border-red-500" : "border-gray-200"
                  }`}
                  required
                />
                {formErrors.Name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.Name}</p>
                )}
              </div>

              <div>
                <label htmlFor="Email" className="block mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="Email"
                  name="Email"
                  value={formData.Email}
                  onChange={handleChange}
                  placeholder="Your Email here"
                  className={`w-full p-4 rounded-lg border ${
                    formErrors.Email ? "border-red-500" : "border-gray-200"
                  }`}
                  required
                />
                {formErrors.Email && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.Email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="Message" className="block mb-2">
                  Message
                </label>
                <textarea
                  id="Message"
                  name="Message"
                  value={formData.Message}
                  onChange={handleChange}
                  placeholder="Write your message here..."
                  className={`w-full p-4 rounded-lg border h-48 ${
                    formErrors.Message ? "border-red-500" : "border-gray-200"
                  }`}
                  required
                />
                {formErrors.Message && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.Message}
                  </p>
                )}
              </div>

              {error && <div className="text-red-500">{error}</div>}
              {success && (
                <div className="text-green-500">Message sent successfully!</div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-900 text-white py-3 px-8 rounded-lg hover:bg-blue-800 transition-colors"
                >
                  {isLoading ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
